import { FC, ReactNode } from 'react';
import { Path } from 'react-hook-form';
import { Invoice } from 'modules/invoice';
import { ConnectDropTarget } from 'react-dnd';
import { CreateDTO } from 'modules/aftermarket';
import { Nullable, SelectOption, ValueOf } from 'global';
import { VendorOpportunity } from 'modules/vendorOpportunity';
import { FACT_NAMES_BOOLEAN, FACT_NAMES_DATE, FACT_NAMES_NUMERIC, FACT_NAMES_STRING } from '@trnsact/business-criteria';
import {
  ProposalStatus,
  ProposalProduct,
  ProposalSaleType,
  SubsidyInformation,
  ProposalMenuOptionType,
  SubsidyCapConfiguration,
  CreditSubmissionDecisionFeeType,
  GetDealInfoFromExternalDmsResponse,
} from '@trnsact/trnsact-shared-types';

import { DeskingState } from './model/types';
import { SaleModeValues } from './context/saleMode/saleModeContext';
import { FinanceFormValues, SaleType, MarkupType } from './shared';
export * from './shared/financeStructure/types';

export type ProductId = string;
export type OptionName = string;
export type Term = string;
export type MenuName = string;
export type FinanceProgramId = string;
export type ProposalMenuId = string;
export type SubmissionId = string;

export enum BuilderViewType {
  Finance = 'Finance',
  AftermarketMenu = 'AftermarketMenu',
}

export enum SaleStructureType {
  Finance,
  AftermarketMenu,
}

export enum SendVia {
  Email = 'Email',
  Text = 'Text',
  TextAndEmail = 'TextAndEmail',
}

export enum EquipmentActionsMode {
  Create = 'create',
  Update = 'update',
}

export enum CcEmailOptionGroups {
  CustomerContacts = 'Customer Contacts',
  DealershipContacts = 'Dealership Contacts',
  OtherContacts = 'Other Contacts',
}

export interface Fee {
  fee: number;
  financed: boolean;
  identifier: string;
}

export interface FeeFormValue extends Fee {
  type: CreditSubmissionDecisionFeeType;
}

export interface Markup {
  identifier: string;
  markupFlat?: number;
  markupPercentage?: number;
}

export interface ContactForm {
  email: string;
  phone: string;
  state: string;
  title: string;
  selectedContact: string;
  fullName: Nullable<string>;
  businessName: Nullable<string>;
  dealerLocation: Nullable<string>;
  assignDealer: Nullable<SelectOption>;
}

export interface CcEmailOption {
  fullName: string;
  email: string;
  title: string;
  group: CcEmailOptionGroups;
}

export interface Pricing {
  rate: number;
  term: number;
  overages?: number;
  productCost?: number;
  totalProfit?: number;
  dealerMarkup: number;
  customerRate: number;
  financeProfit: number;
  amountDueToday?: number;
  profitPercentage?: number;
  aftermarketProfit?: number;
  isSubsidized: boolean;
  subsidyCapConfiguration?: SubsidyCapConfiguration;
  subsidyInformation?: SubsidyInformation;
  menu: Nullable<AftermarketMenu>;
  costOfDeferredPayments: number;
  paymentAmountPerPeriod: number;
  paymentAmountPerPeriodWithoutDealerMarkup: number;
}

export interface MenuCardsProps {
  isCalculating: boolean;
  financeAmount: number;
  isSectionOpen?: boolean;
  isFinanceExcluded?: boolean;
  isMAALessThenFinanceAmount?: boolean;
  builder?: Record<Term, AftermarketMenu>;
  financePricing: Record<Term, Pricing>;
  menuPricing: Record<Term, Record<MenuName, Pricing>>;
}

export interface SaleDetailsForm {
  fees: number;
  netRate: number;
  salesTax: number;
  saleType: SaleType;
  invoiceTotal: number;
  financeAmount: number;
  equipmentSubtotal: number;
  downPayment: { flat: number; percent: number };
}

interface EquipmentForm {
  selectedEquipment: string;
  year: string;
  make: string;
  model: string;
  class: string;
  stockId: string;
  newUsed: string;
  mileage: string;
  quantity: string;
  description: string;
  retailPrice: string;
  serialNumberVin: string;
  totalWholesaleCost: string;
  assets?: File[];
}

type FinanceQuoteForm = FinanceFormValues['financeQuote'];

interface SendViaForm {
  type: SendVia;
}

interface FinanceProgramDisplaySettings {
  productPrice: boolean;
  productDetails: boolean;
  showLenderLogo: boolean;
  showLenderName: boolean;
  showBestOptions: boolean;
  showInterestRate: boolean;
  excludeEquipmentSales: boolean;
  shouldRedirectToOCA: boolean;
  lenderProfileId: string;
  showFinanceProgramDescription: boolean;
}

interface SubmissionForm {
  selectedSubmissionId: Nullable<string>;
  maxApprovalAmount: Nullable<number>;
}

export interface FinanceProgramFormValues {
  submission: SubmissionForm;
  contact: ContactForm;
  saleDetails: SaleDetailsForm;
  equipment: EquipmentForm;
  financeQuote: FinanceQuoteForm;
  sendVia: SendViaForm;
  displaySettings: FinanceProgramDisplaySettings;
  ccEmail: CcEmailOption[];
  financeProgramDescription: { editorValue: any; apiValue: string };
  notesToBuyer?: string;
}

export interface CommonMenuPriceValues {
  cost: number;
  isTotal?: boolean;
  retailCost: number;
  rateFactor: number;
  markup: {
    markup: number;
    type: MarkupType;
  };
  minRetailCost?: number | null;
  maxRetailCost?: number | null;
}

export type ProductPrisingFields = 'cost' | 'markup.markup' | 'retailCost' | 'rateFactor';

export interface SubmitOptions {
  isDraft: boolean;
}

export enum DnDTypes {
  ProposalProduct = 'proposalProduct',
  MenuOption = 'menuOption',
  ProductInMenu = 'productInMenu',
}

export enum AftermarketProductTypes {
  Simple = 'Simple',
  Gaig = 'GAIG',
  Ntp = 'NTP',
  TruckMasterBuckeye = 'TRUCK_MASTER_BUCKEYE',
  TruckSuite = 'TRUCK_SUITE',
}

export type PrisingConfig = {
  isShowRate: boolean;
  isRateDisabled: boolean;
  isShowCost: boolean;
  isCostDisabled: boolean;
  isShowMarkup: boolean;
  isMarkupDisabled: boolean;
  isShowRetail: boolean;
  isRetailDisabled: boolean;
};

export type PrisingConfigByVendor = Record<AftermarketProductTypes, Partial<PrisingConfig>>;

export interface ProposalProductCardProps {
  product: ProposalProduct;
  equipmentCost?: number;
  products: ProposalProduct[];
  type?: 'simple' | 'forEdit';
  factsToCheck?: FactToCheck[];
  factsSkipped?: FactToCheck[];
  isNeedRunJsonEngine?: number;
  hideFieldsForRender?: Fact[];
  mode: ProposalProductCardModes;
  productConfiguration: ProductConfig;
  prisingConfigByVendor: Partial<PrisingConfigByVendor>;
  updateProductConfiguration: (updateValues: Partial<ProductConfig>) => void;
}

export interface ProposalProductCardByVendorProps extends Omit<ProposalProductCardProps, 'prisingConfigByVendor'> {
  prisingConfig: PrisingConfig;
}

export interface FormSectionProps {
  sectionName: FormSections;
  invoiceAction?: ReactNode;
  refetchProposalProducts?: any;
  ccEmailOptions?: CcEmailOption[];
  externalDealInfo?: GetDealInfoFromExternalDmsResponse;
  isSupportBehaviorSetting?: boolean;
  isSupportSaleDetailsUpdating?: boolean;
  onClearStructureAndMenuOptions?: VoidFunction;
  onContactChanged?: (customer: ContactForm) => void;
  handleUpdateCollapse?: (section: FormSections, nextValue: boolean) => void;
  handleUpdateOpenStatus?: (section: FormSections, nextValue: boolean) => void;
  formFieldsConfiguration?: Partial<Record<Path<FinanceProgramFormValues>, boolean>>;
  handleChangeSelectedProductsConfiguration?: (configField: string, nextValue: ProductConfigFactValue) => void;
}

export interface EmailRecipientsProps {
  ccEmailOptions: CcEmailOption[];
}

export enum ProposalProductCardModes {
  Desking = 'desking',
  Constructor = 'constructor',
}

export const ProposalProductCoverageTermDefaultToFinanceTermOption = 'Default to Finance Term';

export enum FormSections {
  Contact = 'contact',
  SaleDetails = 'saleDetails',
  Equipment = 'equipment',
  Structure = 'structure',
  MenuBuilder = 'menuBuilder',
  SendVia = 'sendVia',
  NotesToBuyer = 'notesToBuyer',
}

export enum EquipmentSourcedRequiredOptionsValue {
  DTRSourced = 'DTR-Sourced',
  NonDTRSourced = 'Non DTR-Sourced',
}

export interface UserActionsEvents {
  onSyncEquipment: () => Promise<void>;
  onContactChanged: (customer: ContactForm) => void;
  onEquipmentCreated: (equipmentId: string) => Promise<void>;
  onEquipmentUpdated: (equipmentId: string) => Promise<void>;
  onEquipmentRemoved: (equipmentId: string) => Promise<void>;
}

export type DeskingConfigurationBaseOnAccountFormFieldsRequire = Partial<
  Record<Path<FinanceProgramFormValues>, { value: boolean; message?: string }>
>;

export interface DeskingConfigurationBaseOnAccount {
  saleMode: SaleModeValues;
  displaySettings: Partial<FinanceProgramDisplaySettings>;
  state: Pick<DeskingState, 'menuBuilderViewType'>;
  notifications: Partial<{ menuBuilder: ReactNode }>;
  actionRestrictions: Partial<{ disableFinanceStructure: boolean }>;
  formFieldsRequire: DeskingConfigurationBaseOnAccountFormFieldsRequire;
}

export interface BasePanelProps {
  accountId: string;
  invoiceAction?: ReactNode;
  externalSaleType?: SaleType;
  externalEquipmentId?: Nullable<string>;
  decisionId?: Nullable<string>;
  proposalId?: Nullable<string>;
  vo?: Nullable<VendorOpportunity>;
  inventoryActions?: Nullable<any>;
  ccEmailOptions?: CcEmailOption[];
  isSupportBehaviorSetting?: boolean;
  actions?: Partial<UserActionsEvents>;
  isProductCreationAvailable?: boolean;
  isSupportSaleDetailsUpdating?: boolean;
  refetchProposalProducts?: Nullable<any>;
  initialLenderProfileId?: Nullable<string>;
  initialFinanceProgramId?: Nullable<string>;
  configurationByAccount?: Partial<DeskingConfigurationBaseOnAccount>;
  onCreateProposalProduct: (dto: CreateDTO) => Promise<Nullable<string>>;
  allAvailableCcEmailOptions?: ValueOf<FinanceProgramFormValues, 'ccEmail'>;
  formFieldsConfiguration?: Partial<Record<Path<FinanceProgramFormValues>, boolean>>;
  customSections?: Partial<
    Record<
      FormSections,
      {
        render?: (
          handleChangeSelectedProductsConfiguration: (configField: string, nextValue: ProductConfigFactValue) => void,
          configurationByAccount?: Partial<DeskingConfigurationBaseOnAccount>
        ) => ReactNode;
        isHidden?: boolean;
      }
    >
  >;
  panelFormCustomActions?: {
    render?: (
      onSubmitForm: ({ isDraft }: SubmitOptions) => Promise<void>,
      isProposalLoading: boolean,
      hasUnavailableProducts: boolean
    ) => ReactNode;
    isHidden?: boolean;
  };
  onFormValuesChanged?: (formValues: FinanceProgramFormValues) => void;
}

export interface DeskingSubmitOptions extends SubmitOptions {
  onDeskingReset: VoidFunction;
  form: FinanceProgramFormValues;
  pricing: Record<Term, Pricing>;
  builder: Record<Term, AftermarketMenu>;
  commonProductsConfiguration: Partial<ProductConfig>;
  parentProposalId?: Nullable<string>;
  productsConfigInMenu: Record<OptionName, Record<ProductId, ProductConfig>>;
}

export interface PanelFormContainerProps extends BasePanelProps {
  userEmail: string;
  isProposalLoading: boolean;
  invoice?: Nullable<Invoice>;
  creditApplication?: Nullable<any>;
  onSubmitDesking: (options: DeskingSubmitOptions) => Promise<void>;
  externalDealInfo?: GetDealInfoFromExternalDmsResponse;
}

export interface PanelProps extends BasePanelProps {
  formDefaultValues?: FinanceProgramFormValues;
  createProductSlot?: ReactNode;
  externalDealInfo?: GetDealInfoFromExternalDmsResponse | null;
}

export enum ShowTypes {
  All = 'ALL',
  GAP = 'GAP',
  None = 'NONE',
  Roadside = 'ROADSIDE',
  Insurance = 'INSURANCE',
  ServiceContract = 'SERVICE_CONTRACT',
}

export interface ProductsFiltersDealer {
  search: string;
  vendors: string[];
  showOnly: ShowTypes;
}

export interface ProductsFiltersAftermarketPartner {
  search: string;
  showOnly: ShowTypes;
}

export interface EquipmentDynamicFieldsProps {
  error?: boolean;
  label?: ReactNode;
  fact: FactToCheck;
  disabled?: boolean;
  tooltip?: ReactNode;
  dataTestId?: string;
  helperText?: string;
  options?: SelectOption[];
  value: ProductConfigFactValue;
  onClick?: (fact: FactToCheck) => void;
  onChange: (nextValue: ProductConfigFactValue) => void;
}

export type Fact = FACT_NAMES_BOOLEAN | FACT_NAMES_NUMERIC | FACT_NAMES_DATE | FACT_NAMES_STRING | string;

export interface Condition {
  fact: Fact;
  value: string;
  operator: string;
  factName?: string;
  exactMatch?: string;
  runEngineOnNull?: any;
  requiredForQuote?: boolean;
}

export interface AftermarketEvent {
  type: string;
  params?: Record<string, any>;
}

export interface Criteria {
  name: string;
  event: AftermarketEvent;
  conditions: {
    formRules: Condition[];
    jsonRules: {
      conditions: {
        all: Condition[];
      };
    };
  };
}

export type FactToCheckTypes = 'boolean' | 'numeric' | 'string';

export interface FactToCheck {
  factKey: Fact;
  rule: Condition;
  type: FactToCheckTypes;
  options?: string[] | undefined | null;
}

export type ProductsDynamicFieldsRender = {
  meta: Record<Fact, { options: string[] }>;
  render: Record<Fact, { fact: FactToCheck; fieldComponent: FC<EquipmentDynamicFieldsProps> }>;
};

export enum AftermarketCatalogDetailsKeys {
  ProductSummary = 'productSummary',
  ParagraphDescription = 'paragraphDescription',
  CoverageDetails = 'coverageDetails',
  EligibilityAndInclusions = 'eligibilityAndInclusions',
  LimitationsOfLiability = 'limitationsOfLiability',
  LegalDisclaimer = 'legalDisclaimer',
}

export interface CatalogDetailsToRenderAsLinkConfig {
  key: AftermarketCatalogDetailsKeys;
  label: string;
}

export interface NotifyAboutRemovedProductsDialogData {
  removedProducts: ProposalProduct[];
}

export interface PaymentOptionTerm {
  term: string;
  index: number;
}

export type ProductConfig = Record<Fact | string, any>;

export interface PaymentInfoRenderConfig {
  label: string;
  infoMessage?: ReactNode;
  value: Nullable<number>;
  hideInfoMessage?: boolean;
  formatter: (value: Nullable<number | string>) => string;
}

export interface AftermarketMenuOption {
  name: string;
  ordinal: number;
  payment: Pricing;
  titleColor: string;
  description: string;
  titleBgColor: string;
  menuOptionDetail: any;
  type?: ProposalMenuOptionType;
  products: ProposalProduct[];
  proposalMenuOptionId: string;
}

export interface AftermarketMenu {
  name: string;
  description: string;
  proposalMenuId: string;
  menuOptions: AftermarketMenuOption[];
}

export interface MenuOptionCardBodyProps {
  mode: ProposalProductCardModes;
  menuOption: AftermarketMenuOption;
  commonPrice: CommonMenuPriceValues;
  productsDropContainer: ConnectDropTarget;
  productsConfiguration?: Record<ProductId, ProductConfig>;
  onOpenProductDetailsDialog?: (product: ProposalProduct) => void;
  onRemoveProduct?: (productId: ProductId, currentMenuOption: AftermarketMenuOption) => void;
  onRemoveAddon?: (product: ProposalProduct, optionName: OptionName, title: string) => void;
  onSwitchProducts: (options: { menuName: OptionName; from: number; to: number }) => void;
}

export interface SendProposalData {
  sendViaEmail: boolean;
  sendViaText: boolean;
  phoneNumber: string;
  ccEmail: string[];
  email?: string;
}

export interface Submission {
  id: string;
  decision: string;
  lenderName: string;
  approvedAmount: Nullable<number>;
  proposals: {
    proposalId: string;
    saleType: ProposalSaleType;
    status: ProposalStatus;
    createdDateTime: string;
  }[];
}

export interface ConvertParams {
  isExcludeEquipmentSales: boolean;
  financeProgramDescription: string;
  proposalMenu: Nullable<AftermarketMenu>;
  proposalProductConfigurationsInMenu: ProductConfig;
  program: ValueOf<FinanceProgramFormValues, 'financeQuote'>;
  saleDetails: ValueOf<FinanceProgramFormValues, 'saleDetails'>;
  submission: ValueOf<FinanceProgramFormValues, 'submission'>;
}

export interface FailedRule {
  fact: string;
  actual: string;
  operator: string;
  expected: any;
}

export enum JSONRuleOperators {
  In = 'in',
  NotIn = 'notIn',
  Equal = 'equal',
  Length = 'length',
  NotEqual = 'notEqual',
  LT = 'lessThan',
  GT = 'greaterThan',
  LTI = 'lessThanInclusive',
  GTI = 'greaterThanInclusive',
}

export type ProductConfigFactValue = string | number;
